import config from 'config/config';
import { LanguageContext } from 'context';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { LanguageTypes } from 'types/global';
import { getTranslatedRoutes } from './routes';

export const selectedLanguage = () => window.globalCountry;

export const getBrowserLanguage = (defaultCountryCode: string = 'pl') => {
  return (
    [
      ...(window.navigator.languages || []),
      window.navigator.language,
      // @ts-ignore
      window.navigator.browserLanguage,
      // @ts-ignore
      window.navigator.userLanguage,
      // @ts-ignore
      window.navigator.systemLanguage,
    ]
      .filter(Boolean)
      .map(language => language.substr(0, 2))
      .find(language => config.languages.includes(language)) || defaultCountryCode
  );
};

export const getCookieLanguage = () => localStorage.getItem('language-code');

export const setCookieLanguage = (language: LanguageTypes) => localStorage.setItem('language-code', language);

export const getInitialLanguage = () => {
    const pathname = window.location.pathname;

    // If exactly "/pl", set language to "pl" to fix the unexpected header logo link behaviour.
    if (pathname === "/pl") {
        return "pl";
    }

    // Extract first path segment (language prefix), ensuring it's always a string to satisfy Typescript runtime.
    const urlPathLang = window.location.pathname.split('/')[1] || "";
    const supportedLanguages: string[] = ['en', 'de', 'pl', 'fr'];

    // If the URL contains a valid language, return it.
    if (supportedLanguages.includes(urlPathLang)) {
        return urlPathLang;
    }

    // Final fallback.
    return selectedLanguage() || getCookieLanguage() || getBrowserLanguage() || "en";
};

export const getAcceptedLanguages = () => config.languages;

export const hasBaseline = (language: LanguageTypes) => language !== 'en';

export const hasLanguagePrefix = (language: LanguageTypes, location: string) => {
  const allLanguages = getAcceptedLanguages();
  const [first] = location.replace('/', '').split('/') as unknown as LanguageTypes[];
  return allLanguages.includes(first);
};

export const isDefaultLanguage = (lang: string) => lang === 'en';

export const useRouteTranslations = () => {
  const { language } = useContext(LanguageContext);
  const { pathname } = useLocation();

  const translateSlug = (route: string) => {
    return (window.globalCache.languageRoutes && window.globalCache.languageRoutes[language] && window.globalCache.languageRoutes[language][route]) || '';
  };

  const translatePath = (languageCode: LanguageTypes) => {
    let path = pathname;
    if (path.startsWith('/')) {
      path = path.slice(1);
    }
    if (path.endsWith('/')) {
      path = path.slice(0, path.length - 1);
    }
    const languageRoutes = getTranslatedRoutes(window.globalCache.items, language);
    const splittedPath = path.split('/') as string[];

    const realSplittedPath = hasBaseline(language) ? splittedPath.splice(1, splittedPath.length) : splittedPath;

    const translatedPath = realSplittedPath
      .map(path => {
        const slugToTranslate = Object.keys(languageRoutes[language]).find(slug => path === languageRoutes[language][slug]);
        return slugToTranslate ? languageRoutes[languageCode][slugToTranslate] : '';
      })
      .filter(item => item)
      .join('/');

    return `${hasBaseline(languageCode) ? `/${languageCode}` : ''}/${translatedPath}`;
  };

  return { translateSlug, translatePath };
};
